import { Auth } from 'Src/ng2/shared/auth/auth.service';
import { IDistrict } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { Injectable } from '@angular/core';
import { CanActivate, UrlTree, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { ModalsService } from 'Src/ng2/shared/modals/modals.service';
import { SessionStorageService } from 'Src/ng2/shared/services/web-storage/session-storage/session-storage.service';

@Injectable()
export class DistrictPickerGuard implements CanActivate {
  constructor (
    private router: Router,
    private auth: Auth,
    private modalsService: ModalsService,
    private sessionStorageService: SessionStorageService,
  ) {}

  canActivate (): Observable<UrlTree|boolean> {
    const authenticated = this.auth.isAuthenticated();
    if (authenticated) {
      // Get user accessible districts
      return from(this.auth.getUserDistricts()).pipe(
        switchMap((districts) => {
          if (districts.length === 1) {
            return this.handleSingleDistrictAccess(districts);
          } else if (districts.length > 1) return of(true);
          else return this.handleNoDistrictAccess();
        }),
        catchError(this.handleError.bind(this)),
      );
    }
    // redirect to login if not authenticated
    return of(this.router.createUrlTree(['/login']));
  }


  private handleSingleDistrictAccess (districts: IDistrict[]): Observable<UrlTree|boolean> {
    // manually set to the first district if user only has access to 1 district
    this.sessionStorageService.setItem('currentDistrict', districts[0]._id);
    return of(this.router.createUrlTree(['/home']));
  }

  private handleNoDistrictAccess (): Observable<UrlTree|boolean> {
    const message = 'There isn\'t any district you can access. Please contact your administrator';
    this.showRedirectModal(message);
    return of(false);
  }

  private handleError (): Observable<UrlTree|boolean> {
    const message = 'There was an error retrieving your user districts. Please try again.';
    this.showRedirectModal(message);
    return of(false);
  }

  private showRedirectModal (message: string): void {
    const title = 'Unauthorized Access';
    this.modalsService.openErrorModal({ title, message })
      .afterClosed()
      .pipe(
        take(1),
        tap(() => this.auth.logout()))
      .subscribe();
  }
}
